import { sharedIntegrationsAvailable } from '@mygpt/app/config/integrations';
import { Button, Card, Center, Container, Loader, Stack, Text, Title } from '@mantine/core';
import {
  IconCheck,
  IconDatabase,
  IconLoader2,
  IconRobot,
  IconSettings,
  IconSparkles,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  addDatasets,
  addDatasetsFiles,
  AvatarByUuidResponse,
  bindBotDatasets,
  createBot as createAgentBot,
  createWebConnector,
  generatePrompt,
  getAvatarByUuid,
  getDatasetsFilesList,
  getRobot,
  getWebConnectors,
  updateRobotConfigs,
} from '@mygpt/app';
import { setResourcePrinciple } from '@mygpt/app/apis/services/admin';
import { DEFAULT_WIDGET_CONFIGS } from '@/components/Widget/Embed';
import { useGPTStore } from '@/store';
import { useAdminStore } from '@/store/admin';

const DEFAULT_AGENT_PROMPT = `# Role
You are a highly intelligent and professional assistant designed to assist users with their tasks. Your role is to provide accurate, clear, and concise responses to user queries while maintaining a friendly and helpful tone.

# General
When responding to user inputs:
1. Understand the user's request or task by analyzing the input carefully.
2. Provide a detailed and relevant response that directly addresses the user's needs.
3. If the task requires reasoning, ensure to explain the reasoning process before presenting the conclusion.
4. Maintain clarity and conciseness in your responses, avoiding unnecessary information.

# Output Format
- For simple queries, provide a direct and concise answer.
- For tasks requiring structured data, use JSON format with clearly defined fields.
- For complex tasks, break down the response into logical steps or sections for better understanding.

# Example Interaction
**User Input:** What is the capital of France?
**Assistant Response:** The capital of France is Paris.`;

export interface SetupStep {
  key: string;
  label: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  error?: string;
  icon?: any;
}

const AvailableAutoSetupPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { assetScope } = useGPTStore();
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const isCloudMode = !isLocalMode;

  // 添加自定义样式
  const customStyles = `
    @keyframes gentleGlow {
      0% {
        box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4);
        opacity: 1;
      }
      100% {
        box-shadow: 0 0 0 8px rgba(139, 92, 246, 0);
        opacity: 0.8;
      }
    }

    @keyframes backgroundPulse {
      0%, 100% {
        box-shadow: 0 0 20px 10px rgba(139, 92, 246, 0.1);
        transform: scale(1);
      }
      50% {
        box-shadow: 0 0 40px 20px rgba(139, 92, 246, 0.2);
        transform: scale(1.05);
      }
    }
  `;

  const [steps, setSteps] = useState<SetupStep[]>([
    {
      key: 'get_avatar',
      label: t('auto_setup.steps.get_avatar'),
      description: t('auto_setup.steps.get_avatar_desc'),
      status: 'pending',
      icon: IconUser,
    },
    {
      key: 'create_avatar',
      label: t('auto_setup.steps.create_avatar'),
      description: t('auto_setup.steps.create_avatar_desc'),
      status: 'pending',
      icon: IconRobot,
    },
    {
      key: 'create_dataset',
      label: t('auto_setup.steps.create_dataset'),
      description: t('auto_setup.steps.create_dataset_desc'),
      status: 'pending',
      icon: IconDatabase,
    },
    {
      key: 'update_config',
      label: t('auto_setup.steps.update_config'),
      description: t('auto_setup.steps.update_config_desc'),
      status: 'pending',
      icon: IconSettings,
    },
  ]);

  const uuid = searchParams.get('uuid');

  const [isRunning, setIsRunning] = useState(false);
  const setupExecutingRef = useRef(false); // 使用 ref 跟踪执行状态，避免闭包问题
  const [botId, setBotId] = useState<string>('');
  const [avatarInfo, setAvatarInfo] = useState<AvatarByUuidResponse | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const updateStepStatus = (stepKey: string, status: SetupStep['status'], error?: string) => {
    setSteps((prev) => prev.map((step) => (step.key === stepKey ? { ...step, status, error } : step)));
  };

  // 获取数字人信息
  const fetchAvatarInfo = async (uuid: string) => {
    setAvatarError(null);
    updateStepStatus('get_avatar', 'running');

    try {
      const info = await getAvatarByUuid(uuid);
      setAvatarInfo(info);
    } catch (error: any) {
      console.error('Failed to fetch avatar info:', error);
      setAvatarError(error.message);
      updateStepStatus('get_avatar', 'error', error.message);
    }
  };

  const generatePromptForBot = async () => {
    try {
      // 确保avatarInfo已经获取到
      if (!avatarInfo) {
        throw new Error('Avatar information not retrieved, please try again later');
      }

      const promptInput = `以下の情報に基づいて専門のAIアシスタントを作成してください：
- 名前：${avatarInfo.name}
- 用途：${avatarInfo.purpose}
- 会社URL：${avatarInfo.company_url || 'なし'}
- 目標：会社のイメージを代表できるアシスタントを作成し、会社の製品やサービスに関する質問に答えられるようにする`;

      const requestBody = {
        prompt_in: promptInput,
        cur_prompt: null,
        stream: false,
      };

      const response = await generatePrompt(requestBody);

      if (response?.prompt) {
        const invalidPatterns = ['unclear', 'incomplete', 'provide more details', 'clarify your request'];
        const isInvalid = invalidPatterns.some((p) => response.prompt.toLowerCase().includes(p));
        return isInvalid ? DEFAULT_AGENT_PROMPT : response.prompt;
      } else {
        return DEFAULT_AGENT_PROMPT;
      }
    } catch (error: any) {
      const errorMsg = error.message;
      updateStepStatus('create_avatar', 'error', errorMsg);
      throw error;
    }
  };

  const createBot = async (generatedPrompt: string) => {
    try {
      if (!avatarInfo) {
        throw new Error('Avatar information not retrieved');
      }

      const requestBody = {
        name: avatarInfo.name,
        discrible: avatarInfo.company_url || avatarInfo.name,
        subject_name: avatarInfo.name,
        prompt: generatedPrompt || '',
        url: null,
        dataset_ids: null,
        init_default_datasets: false,
        ai_type: 'public',
        ai_model: 'agent',
        robot_type: 'agent',
        agent_function_call_ids: [],
        // Cloud版传入公司ID：有组织ID则传入，否则不传（表示个人资产）
        ...(isCloudMode && assetScope && { company_id: assetScope }),
      };

      const response = await createAgentBot(requestBody);

      if (response?.id) {
        // Cloud企业模式下初始化 bot 权限为私有（仅创建者可见）
        if (assetScope) {
          try {
            const { userOgsId } = useAdminStore.getState();
            await setResourcePrinciple({
              userIds: [userOgsId || ''],
              departmentIds: [],
              companyAuthorized: false,
              dataId: response.id,
              dataType: 'BOT',
              authorityCode: 'BOT_VIEW',
            });
          } catch (e) {
            console.error('Failed to init bot permission:', e);
          }
        }
        setBotId(response.id);
        return response.id;
      } else {
        throw new Error('Failed to create bot');
      }
    } catch (error: any) {
      const errorMsg = error.message;
      updateStepStatus('create_avatar', 'error', errorMsg);
      throw error;
    }
  };

  const createDataset = async () => {
    try {
      if (!avatarInfo) {
        throw new Error('Avatar information not retrieved');
      }

      const datasetParams = {
        name: avatarInfo.name,
        description: avatarInfo.company_url || avatarInfo.name,
        embedding_model_name: 'text-embedding-3-large',
        // Cloud版传入公司ID：有组织ID则传入，否则不传（表示个人资产）
        ...(isCloudMode && assetScope && { company_id: assetScope }),
      };

      const response = await addDatasets(datasetParams);

      if (response.data?.id) {
        return response.data.id;
      } else {
        throw new Error('Failed to create dataset');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message;
      updateStepStatus('create_dataset', 'error', errorMsg);
      throw error;
    }
  };

  const downloadMarkdownContent = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      return await response.text();
    } catch (error: any) {
      console.error('Download markdown file failed:', error);
      updateStepStatus('create_dataset', 'error', error.message);
      throw error;
    }
  };

  const uploadDocuments = async (datasetId: string) => {
    try {
      // 暂时跳过文档学习路径，代码保留但不使用
      // 如果没有company_markdown_url，跳过学习文档流程
      if (!avatarInfo?.company_markdown_url) {
        console.log('Company markdown URL not found, skipping document upload');
        return [];
      }

      const formData = new FormData();

      try {
        console.log('Downloading company document:', avatarInfo.company_markdown_url);
        const markdownContent = await downloadMarkdownContent(avatarInfo.company_markdown_url);

        // 创建markdown文件
        const blob = new Blob([markdownContent], { type: 'text/markdown' });
        const fileName = `${avatarInfo.name}.md`;
        const file = new File([blob], fileName, { type: 'text/markdown' });
        formData.append('files', file);

        console.log('Company document downloaded successfully, file name:', fileName);
      } catch (error) {
        console.warn('Download company document failed, skipping:', error);
        return [];
      }

      const response = await addDatasetsFiles({
        id: datasetId,
        formData,
        onUploadProgress: (progressEvent) => {
          console.log('Upload progress:', progressEvent);
        },
      });

      if (response.data && response.data.length > 0) {
        return response.data;
      } else {
        throw new Error('Failed to upload documents');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message;
      updateStepStatus('create_dataset', 'error', errorMsg);
      throw error;
    }
  };

  const createWebConnectorIntegration = async (datasetId: string) => {
    try {
      if (!avatarInfo) {
        throw new Error('Avatar information not retrieved');
      }

      // 如果没有company_url，跳过web connector创建
      if (!avatarInfo.company_url) {
        console.log('Company URL not found, skipping web connector creation');
        return null;
      }

      const webConnectorParams = {
        name: avatarInfo.name,
        type: 'web_connector',
        config: {
          enable_sync: false,
          interval_seconds: 2592000, // 30天
          notification_emails: [],
          web_connector: {
            crawl_mode: 'list' as const,
            url_list: [avatarInfo.company_url],
            is_spa: false,
          },
        },
      };

      console.log('Creating web connector:', webConnectorParams);
      const response = await createWebConnector(datasetId, webConnectorParams);

      if (response.data?.id) {
        console.log('Web connector created successfully:', response.data.id);
        return response.data.id;
      } else {
        throw new Error('Failed to create web connector');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message;
      updateStepStatus('create_web_connector', 'error', errorMsg);
      throw error;
    }
  };

  const waitForWebConnectorComplete = async (datasetId: string) => {
    try {
      const maxAttempts = 60; // 最多检查60次，每次间隔5秒，总共5分钟
      const checkInterval = 5000; // 5秒间隔
      let attempts = 0;

      while (attempts < maxAttempts) {
        try {
          const response = await getWebConnectors(datasetId, {
            page: 1,
            size: 10,
            order_by: '',
          });

          if (response.data?.items && response.data.items.length > 0) {
            const webConnector = response.data.items[0];
            console.log('Web connector status:', webConnector.file_list_sync_status);

            if (webConnector.file_list_sync_status === 'completed') {
              console.log('Web connector sync completed');
              return true;
            } else if (webConnector.file_list_sync_status === 'failed') {
              throw new Error('Web connector sync failed');
            }
          }

          attempts++;
          if (attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, checkInterval));
          }
        } catch (error) {
          console.warn('Error checking web connector status:', error);
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, checkInterval));
          }
        }
      }

      throw new Error('Web connector sync timeout');
    } catch (error: any) {
      const errorMsg = error.message;
      updateStepStatus('wait_web_connector', 'error', errorMsg);
      throw error;
    }
  };

  const waitForLearningComplete = async (datasetId: string) => {
    // 注意：这个函数是create_dataset步骤的一部分，状态更新在startSetup中处理

    try {
      const maxAttempts = 60; // 最多检查60次，每次间隔5秒，总共5分钟
      const checkInterval = 5000; // 5秒间隔
      let attempts = 0;

      while (attempts < maxAttempts) {
        try {
          const response = await getDatasetsFilesList({
            id: datasetId,
            file_type: 'file',
            page: 1,
            size: 100,
          });

          const files = response.data?.items || [];

          if (files.length === 0) {
            throw new Error('No uploaded documents found');
          }

          // 检查所有文件是否都学习完成
          const allComplete = files.every((file) => file.file_status === 'complete');
          const hasFailed = files.some((file) => file.file_status === 'fail');

          if (hasFailed) {
            const failedFiles = files.filter((file) => file.file_status === 'fail');
            const failedReasons = failedFiles.map((file) => `${file.filename}: ${file.failed_reason}`).join('; ');
            throw new Error(`Some documents failed to learn: ${failedReasons}`);
          }

          if (allComplete) {
            console.log('All documents learned successfully');
            return;
          }

          // 显示当前学习进度
          const completedCount = files.filter((file) => file.file_status === 'complete').length;
          const learningCount = files.filter((file) => file.file_status === 'process').length;
          console.log(
            `Document learning progress: ${completedCount}/${files.length} completed, ${learningCount} learning`
          );
        } catch (error: any) {
          console.error('Failed to check learning status:', error);
          // 如果是网络错误，继续重试
          if (error.message?.includes('Network Error') || error.message?.includes('timeout')) {
            attempts++;
            await new Promise((resolve) => setTimeout(resolve, checkInterval));
            continue;
          }
          throw error;
        }

        attempts++;
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }

      throw new Error('Document learning timeout, please check learning status in dataset management later');
    } catch (error: any) {
      const errorMsg = error.message;
      updateStepStatus('create_dataset', 'error', errorMsg);
      throw error;
    }
  };

  const bindDatasetToBot = async (botId: string, datasetId: string) => {
    // 注意：这个函数是create_dataset步骤的一部分，状态更新在startSetup中处理

    try {
      await bindBotDatasets({
        aiId: botId,
        datasetIds: [datasetId],
        type: 'bind',
      });
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message;
      updateStepStatus('create_dataset', 'error', errorMsg);
      throw error;
    }
  };

  const getBotConfig = async (botId: string) => {
    try {
      const response = await getRobot({ aiId: botId });

      if (response?.data) {
        console.log('robot configs:', response.data);
        return response.data;
      } else {
        throw new Error('Failed to get bot configuration');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message;
      updateStepStatus('update_config', 'error', errorMsg);
      throw error;
    }
  };

  const updateBotConfig = async (botId: string, botConfig: any) => {
    // 注意：这个函数的运行状态已经在startSetup中设置了，这里不需要重复设置

    try {
      if (!botConfig || !avatarInfo) {
        throw new Error('Bot configuration or avatar information not retrieved');
      }

      // 解析现有的widget_configs，使用导出的默认配置
      let existingWidgetConfigs = DEFAULT_WIDGET_CONFIGS;
      try {
        if (botConfig.configs?.widget_configs) {
          const parsed = JSON.parse(botConfig.configs.widget_configs);
          existingWidgetConfigs = { ...DEFAULT_WIDGET_CONFIGS, ...parsed };
        }
      } catch (error) {
        console.warn('Failed to parse existing widget_configs, using default configs:', error);
      }

      // 更新widget_configs中的digitalHuman.image和autoOpenUrls
      const updatedWidgetConfigs = {
        ...existingWidgetConfigs,
        digitalHuman: {
          ...existingWidgetConfigs.digitalHuman,
          image: 'custom',
          textureUrl: avatarInfo?.texture_url,
        },
        chatbot: {
          ...existingWidgetConfigs.chatbot,
          autoOpenUrls: {
            checked: true,
            urls: ['/widget-preview'],
          },
        },
      };

      // 准备更新配置
      const updatedConfigs = {
        ...botConfig.configs,
        enable_2d_digital_person: true,
        widget_configs: JSON.stringify(updatedWidgetConfigs),
      };

      await updateRobotConfigs({
        aiId: botId,
        configs: updatedConfigs,
      });
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message;
      updateStepStatus('update_config', 'error', errorMsg);
      throw error;
    }
  };

  const startSetup = async () => {
    if (isRunning || setupExecutingRef.current) {
      console.log('Running, skipping...', {
        isRunning,
        setupExecuting: setupExecutingRef.current,
      });
      return;
    }

    console.log('Starting auto setup...');
    setIsRunning(true);
    setupExecutingRef.current = true; // 立即设置 ref 标记

    try {
      // 步骤1：获取数字人信息（已在useEffect中执行）
      updateStepStatus('get_avatar', 'completed');

      // 步骤2：创建AI助手（包括生成提示词和创建机器人）
      console.log('Step 2: Creating bot...');
      updateStepStatus('create_avatar', 'running');
      const generatedPrompt = await generatePromptForBot();
      const newBotId = await createBot(generatedPrompt);
      updateStepStatus('create_avatar', 'completed');

      // 步骤3：创建知识库（包括上传文档、学习、绑定）
      console.log('Step 3: Creating dataset...');
      updateStepStatus('create_dataset', 'running');
      const newDatasetId = await createDataset();
      // await uploadDocuments(newDatasetId);
      // await waitForLearningComplete(newDatasetId);
      if (avatarInfo?.company_url) {
        await createWebConnectorIntegration(newDatasetId);
        await waitForWebConnectorComplete(newDatasetId);
      }
      await bindDatasetToBot(newBotId, newDatasetId);
      updateStepStatus('create_dataset', 'completed');

      // 步骤4：配置数字人（开启数字人开关，设置数字人形象）
      console.log('Step 4: Configuring digital human...');
      updateStepStatus('update_config', 'running');
      const botConfig = await getBotConfig(newBotId);
      await updateBotConfig(newBotId, botConfig);
      updateStepStatus('update_config', 'completed');

      console.log('Auto setup completed successfully!');
    } catch (error) {
      console.error('Auto setup failed:', error);
      setupExecutingRef.current = false; // 重置 ref 标记
    } finally {
      setIsRunning(false);
      if (!steps.some((step) => step.status === 'error')) {
        // 只有在没有错误的情况下才保持执行标记
        setupExecutingRef.current = true;
      }
    }
  };

  const isCompleted = steps && steps.length > 0 && steps.every((step) => step.status === 'completed');
  const hasError = steps && steps.length > 0 && steps.some((step) => step.status === 'error');

  // 开始倒计时
  const startCountdown = () => {
    setCountdown(3);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          handleComplete();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 停止倒计时
  const stopCountdown = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
  };

  useEffect(() => {
    // 获取URL参数中的UUID
    if (uuid && !avatarInfo && !avatarError && !isRunning) {
      fetchAvatarInfo(uuid);
    }
  }, []);

  useEffect(() => {
    if (avatarInfo && !isRunning && !setupExecutingRef.current && !avatarError) {
      startSetup();
    }
  }, [avatarInfo]);

  // 监听完成状态，启动倒计时
  useEffect(() => {
    if (isCompleted && !hasError) {
      startCountdown();
    } else {
      stopCountdown();
    }

    // 清理函数
    return () => {
      stopCountdown();
    };
  }, [isCompleted, hasError]);

  const handleComplete = () => {
    if (botId) {
      window.location.href = `/widget-preview?id=${botId}`;
    } else {
      window.location.href = '/';
    }
  };

  const handleRetry = () => {
    // 停止倒计时
    stopCountdown();

    // 完全重置所有状态
    setSteps((prev) =>
      prev.map((step) => ({
        ...step,
        status: 'pending' as const,
        error: undefined,
      }))
    );
    setBotId('');
    setAvatarInfo(null);
    setAvatarError(null);
    setupExecutingRef.current = false; // 重置 ref 标记

    // 重新获取avatar信息并启动流程
    if (uuid) {
      fetchAvatarInfo(uuid);
    }
  };

  const getCurrentActiveStep = () => {
    const runningStepIndex = steps.findIndex((step: SetupStep) => step.status === 'running');
    if (runningStepIndex !== -1) return runningStepIndex;

    // 使用倒序查找最后完成的步骤
    let lastCompletedIndex = -1;
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].status === 'completed') {
        lastCompletedIndex = i;
        break;
      }
    }
    return lastCompletedIndex + 1;
  };

  const getOverallProgress = () => {
    if (!steps || steps.length === 0) return 0;
    const completedSteps = steps.filter((step: SetupStep) => step.status === 'completed').length;
    return Math.round((completedSteps / steps.length) * 100);
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-100 relative overflow-hidden">
        {/* 背景装饰圆形 */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute top-20 left-20 w-32 h-32 bg-violet-400/30 rounded-full blur-2xl"
            style={{ animation: 'backgroundPulse 4s ease-in-out infinite' }}
          ></div>
          <div
            className="absolute top-40 right-20 w-24 h-24 bg-purple-400/20 rounded-full blur-2xl"
            style={{ animation: 'backgroundPulse 3s ease-in-out infinite 0.5s' }}
          ></div>
          <div
            className="absolute bottom-40 left-1/4 w-28 h-28 bg-indigo-400/25 rounded-full blur-2xl"
            style={{ animation: 'backgroundPulse 5s ease-in-out infinite 1s' }}
          ></div>
          <div
            className="absolute bottom-20 right-1/3 w-20 h-20 bg-violet-300/30 rounded-full blur-2xl"
            style={{ animation: 'backgroundPulse 3.5s ease-in-out infinite 1.5s' }}
          ></div>
        </div>

        <Container size="lg" py="xl" className="relative z-10">
          <Center>
            <Stack align="center" gap="lg" style={{ width: '100%', maxWidth: 600 }}>
              {/* 主卡片容器 */}
              {!isCompleted && (
                <Card
                  className="w-full border-0 bg-white/80 backdrop-blur-xl shadow-xl rounded-2xl p-5"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
                  }}
                >
                  {/* 头部区域 */}
                  <div className="text-center mb-6">
                    <div className="flex items-center justify-center mb-3">
                      <div
                        className="w-12 h-12 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg"
                        style={{ boxShadow: '0 6px 20px rgba(139, 92, 246, 0.3)' }}
                      >
                        <IconRobot size={22} className="text-white" stroke={2} />
                      </div>
                    </div>
                    <Title order={1} className="text-xl font-bold text-gray-800 mb-1">
                      {t('auto_setup.title')}
                    </Title>
                  </div>

                  {/* 创建进度区域 */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg flex items-center justify-center shadow-md">
                        <IconSparkles size={14} className="text-white" />
                      </div>
                      <Text size="md" fw={600} className="text-gray-800">
                        {t('auto_setup.progress_title')}
                      </Text>
                    </div>

                    {/* 进度条 */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-2 bg-gray-200/60 rounded-full overflow-hidden backdrop-blur-sm">
                        <div
                          className={`h-full transition-all duration-1000 ease-out rounded-full ${
                            hasError
                              ? 'bg-red-500'
                              : isCompleted
                              ? 'bg-green-500'
                              : 'bg-gradient-to-r from-violet-500 to-violet-600'
                          }`}
                          style={{ width: `${getOverallProgress()}%`, boxShadow: '0 0 8px rgba(139, 92, 246, 0.4)' }}
                        />
                      </div>
                      <Text size="sm" fw={600} className="text-violet-600">
                        {getOverallProgress()}%
                      </Text>
                    </div>
                  </div>

                  {/* 步骤列表 */}
                  {!isCompleted && (
                    <div className="space-y-3">
                      {steps.map((step: SetupStep, index: number) => (
                        <div
                          key={step.key}
                          className={`p-4 rounded-xl border transition-all duration-300 ${
                            step.status === 'completed'
                              ? 'bg-green-50/80 border-green-200 shadow-sm'
                              : step.status === 'running'
                              ? 'bg-violet-50/80 border-violet-200 shadow-md'
                              : step.status === 'error'
                              ? 'bg-red-50/80 border-red-200 shadow-sm'
                              : 'bg-gray-50/50 border-gray-200'
                          }`}
                          style={step.status === 'running' ? { boxShadow: '0 4px 20px rgba(139, 92, 246, 0.15)' } : {}}
                        >
                          <div className="flex items-center gap-3">
                            {/* 步骤图标 */}
                            <div className="flex-shrink-0">
                              {step.status === 'completed' ? (
                                <div
                                  className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-md"
                                  style={{ boxShadow: '0 3px 10px rgba(34, 197, 94, 0.3)' }}
                                >
                                  <IconCheck size={16} className="text-white" />
                                </div>
                              ) : step.status === 'running' ? (
                                <div
                                  className="w-8 h-8 bg-gradient-to-br from-violet-500 to-violet-600 rounded-full flex items-center justify-center"
                                  style={{
                                    animation:
                                      'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite, gentleGlow 1.5s ease-in-out infinite alternate',
                                    boxShadow: '0 3px 15px rgba(139, 92, 246, 0.4)',
                                  }}
                                >
                                  <IconLoader2 size={16} className="text-white animate-spin" />
                                </div>
                              ) : step.status === 'error' ? (
                                <div
                                  className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-md"
                                  style={{ boxShadow: '0 3px 10px rgba(239, 68, 68, 0.3)' }}
                                >
                                  <IconX size={16} className="text-white" />
                                </div>
                              ) : (
                                <div className="w-8 h-8 bg-gray-300/70 rounded-full flex items-center justify-center backdrop-blur-sm">
                                  <Text size="sm" fw={600} className="text-gray-600">
                                    {index + 1}
                                  </Text>
                                </div>
                              )}
                            </div>

                            {/* 步骤内容 */}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Text
                                  size="md"
                                  fw={600}
                                  className={`${
                                    step.status === 'running'
                                      ? 'text-violet-700'
                                      : step.status === 'completed'
                                      ? 'text-green-700'
                                      : step.status === 'error'
                                      ? 'text-red-700'
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {step.label}
                                </Text>
                                {step.status === 'running' && <Loader size="xs" color="violet" type="dots" />}
                              </div>
                              <Text size="sm" className="text-gray-600">
                                {step.description}
                              </Text>
                              {step.error && (
                                <Text size="xs" className="text-red-500 mt-1">
                                  {step.error}
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}

              {/* 完成状态 */}
              {isCompleted && (
                <Card
                  className="w-full border-0 bg-white/80 backdrop-blur-xl shadow-xl rounded-2xl p-8"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
                  }}
                >
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-4 mb-6">
                      {/* 完成图标 */}
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="absolute inset-0 bg-green-400 rounded-full opacity-30 animate-ping"></div>
                          <div
                            className="relative w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg"
                            style={{ boxShadow: '0 6px 20px rgba(34, 197, 94, 0.3)' }}
                          >
                            <IconCheck size={32} className="text-white" />
                          </div>
                        </div>
                      </div>

                      {/* 完成内容 */}
                      <div className="text-left">
                        <Text size="xl" fw={700} className="text-green-700 mb-2">
                          🎉 {t('auto_setup.success')}
                        </Text>
                        <Text size="md" className="text-gray-600">
                          {t('auto_setup.success_desc')}
                        </Text>
                        {countdown !== null && (
                          <Text size="md" fw={600} className="text-violet-600 mt-2">
                            {t('auto_setup.auto_preview_countdown', { countdown })}
                          </Text>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-3">
                      <Button
                        onClick={handleComplete}
                        size="lg"
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold px-8 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                      >
                        <div className="flex items-center gap-3">
                          <IconRobot size={20} />
                          <span className="text-lg">{t('auto_setup.experience_ai_assistant')}</span>
                          <IconSparkles size={16} className="animate-pulse" />
                        </div>
                      </Button>
                      {countdown !== null && (
                        <Button
                          onClick={stopCountdown}
                          variant="outline"
                          size="md"
                          className="border-gray-300 text-gray-600 hover:bg-gray-50 font-medium px-6 py-2 rounded-lg transition-all duration-300 hover:border-gray-400"
                        >
                          <span className="text-sm">{t('auto_setup.cancel_auto_preview')}</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* 错误状态 */}
              {hasError && (
                <div className="flex justify-center p-4">
                  <Button
                    onClick={handleRetry}
                    size="sm"
                    className="bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <div className="flex items-center gap-2">
                      <IconLoader2 size={14} />
                      <span className="text-sm">{t('auto_setup.retry')}</span>
                    </div>
                  </Button>
                </div>
              )}
            </Stack>
          </Center>
        </Container>
      </div>
    </>
  );
};

const AutoSetupPage = () =>
  sharedIntegrationsAvailable ? (
    <AvailableAutoSetupPage />
  ) : (
    <Center h="60vh">
      <Text>Avatar setup is unavailable.</Text>
    </Center>
  );

export default AutoSetupPage;
